import React, { useCallback, useContext } from 'react';
import CypAdhdReviewDocument from './CypAdhdReviewDocument';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { getCypPatientSexAtBirth, cypPeriodsContentApplies } from '../../../utils/conditionChecks';

/**
 * RD_CI_CypAdhd — CYP ADHD Clinical Interview review page (ASMT-21).
 *
 * Thin wrapper: supplies the CI document structure and the CYP AI-inputs
 * panel groups to the shared CypAdhdReviewDocument. The structure mirrors
 * SECTION_PARAGRAPHS in llm_processor_cyp_ci.py (backend source of truth);
 * paragraphs without generated content (e.g. the data-gated Puberty
 * paragraph) are hidden and excluded from the approval denominator by the
 * shared component/hook, so an ungated structure entry is safe.
 *
 * The one patient-dependent entry is Puberty: when the child's sex assigned
 * at birth is recorded as Male or Intersex the backend neither drafts nor
 * keeps that paragraph, and this page drops the entry too (see
 * buildCiDocStructure) so a stale paragraph generated before the sex was
 * recorded can never be shown or approved here.
 *
 * The 'ciReportApproved' flag this page's exit writes is half of the DD
 * unlock condition in cyp_adhd_stage_progress.py.
 */

/** Output key of the Developmental History paragraph about periods. */
const PUBERTY_PARAGRAPH_CODE = 'cyp_ci_DEV_puberty';

// Section titles and codes must match the backend SECTION_PARAGRAPHS /
// prompt_configs_cyp_ci subsection names.
const CI_DOC_STRUCTURE = {
  // One entry per Clinical Interview panel, in panel order (see
  // CI_PANELS in python-functions/constants/cyp_adhd/ci_data_model.py), so
  // the review document reads in the same order the clinician took notes.
  'Presenting Complaint': {
    1: { title: 'Reason for Referral', code: 'cyp_ci_COM_referral_information' },
    2: { title: 'History of Presenting Difficulties', code: 'cyp_ci_COM_history_of_presenting_complaint' },
    3: { title: 'Strengths and Interests', code: 'cyp_ci_COM_strengths_and_interests' },
  },
  'Education History': {
    1: { title: 'School Setting and Attendance', code: 'cyp_ci_EDU_current_schooling' },
    2: { title: 'Learning and Behaviour at School', code: 'cyp_ci_EDU_learning_and_behaviour' },
    3: { title: 'Support and Strategies', code: 'cyp_ci_EDU_support_and_strategies' },
  },
  'Developmental History': {
    1: { title: 'Pregnancy and Birth', code: 'cyp_ci_DEV_pre_natal_and_birth' },
    2: { title: 'Early Years and Milestones', code: 'cyp_ci_DEV_early_years' },
    3: { title: 'Puberty', code: 'cyp_ci_DEV_puberty' },
    4: { title: 'Difficult Life Events', code: 'cyp_ci_DEV_adverse_experiences' },
  },
  'Social History': {
    1: { title: 'Family and Home Life', code: 'cyp_ci_SOC_family_and_home_life' },
    2: { title: 'Lifestyle Factors', code: 'cyp_ci_SOC_lifestyle_factors' },
    3: { title: 'Screens and Gaming', code: 'cyp_ci_SOC_screens_and_gaming' },
    4: { title: 'Friendships and Social Life', code: 'cyp_ci_SOC_friendships_and_social' },
    5: { title: 'Contact with the Law and Personal Safety', code: 'cyp_ci_SOC_contact_with_the_law' },
  },
  'Family History': {
    1: { title: 'Family History', code: 'cyp_ci_FAM_health_conditions' },
  },
  'Medical History': {
    1: { title: 'Physical Health and Hospital Visits', code: 'cyp_ci_MED_health_conditions' },
    2: { title: 'Medications and Allergies', code: 'cyp_ci_MED_medications_and_allergies' },
    3: { title: 'Vision, Hearing and Oral Health', code: 'cyp_ci_MED_vision_hearing_and_oral' },
  },
  'Mental Health History': {
    1: { title: 'Diagnoses and Current Support', code: 'cyp_ci_MHH_diagnoses_and_support' },
    2: { title: 'Safety and Emotional Wellbeing', code: 'cyp_ci_MHH_safety_and_wellbeing' },
  },
  'Mental State Examination': {
    1: { title: 'Summary', code: 'cyp_ci_MSE_summary' },
  },
  'Risk Assessment': {
    1: { title: 'Summary', code: 'cyp_ci_RSK_summary' },
  },
};

// The CYP structured-entry buckets (see llm_processor_cyp_ci.py): the
// reporters are the parent/carer and the school, never the child.
const CI_INPUT_PANEL_GROUPS = [
  ['Your decisions', 'Clinician Decisions'],
  ['Your notes', 'Clinician Notes'],
  ["Parent/carer's report", 'Parent/Carer Prefill'],
  ["School's report", 'School Prefill'],
];

/**
 * The CI document structure for one patient.
 *
 * Returns CI_DOC_STRUCTURE as is for most children. For a child whose header
 * records sex assigned at birth as Male or Intersex, the Puberty entry is
 * removed from Developmental History. The remaining entries keep their
 * original numeric keys: the shared review component uses the keys for
 * ordering only, so a gap does not change what the clinician sees.
 *
 * Fail-open, like every other reader of the gate: Female, "Prefer not to
 * say" or no recorded value keep the full structure, and the Puberty
 * paragraph then shows or hides on whether it has content as before.
 *
 * @param {Object|null|undefined} header - The patient header (ModularPatientDataContext.header)
 * @returns {Object} section title -> { n: { title, code } }
 */
export const buildCiDocStructure = (header) => {
  if (cypPeriodsContentApplies(getCypPatientSexAtBirth(header))) return CI_DOC_STRUCTURE;
  const structure = {};
  Object.entries(CI_DOC_STRUCTURE).forEach(([sectionTitle, entries]) => {
    structure[sectionTitle] = Object.fromEntries(
      Object.entries(entries).filter(([, entry]) => entry.code !== PUBERTY_PARAGRAPH_CODE),
    );
  });
  return structure;
};

export default function RD_CI_CypAdhd() {
  const { header } = useContext(ModularPatientDataContext);
  const buildDocStructure = useCallback(() => buildCiDocStructure(header), [header]);
  return (
    <CypAdhdReviewDocument
      stageKey="clinicalInterview"
      flagPrefix="ci"
      moduleTitle="Clinical Interview"
      editStageCode="CI"
      buildDocStructure={buildDocStructure}
      inputPanelGroups={CI_INPUT_PANEL_GROUPS}
    />
  );
}

export { CI_DOC_STRUCTURE };
